import { useCallback, useEffect, useState } from "react";
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from "@microsoft/signalr";
import { getServiceBaseRoute } from "services/serviceFactory";
import { useAccessToken } from "./useAccessToken";

interface UseSignalRResult {
    connection?: HubConnection;
    status?: HubConnectionState;
    listen: (methodName: string, method: (...args: any[]) => any) => void;
}
const BASE_URL = `${getServiceBaseRoute("iot")}/hubs`;

export const useSignalRHub = (hubName: string): UseSignalRResult => {
    const [connection, setConnection] = useState<HubConnection>();
    const [status, setStatus] = useState<HubConnectionState>();
    const accessToken = useAccessToken();

    useEffect(() => {
        const newConnection = new HubConnectionBuilder()
            .configureLogging(LogLevel.None)
            .withUrl(`${BASE_URL}/${hubName}`, {
                accessTokenFactory: () => accessToken || ""
            })
            .withAutomaticReconnect()
            .build();

        setConnection(newConnection);

        newConnection
            .start()
            .then(() => setStatus(newConnection.state))
            .catch(error => {
                console.error(`Error connecting to SignalR hub '${hubName}':`, error);
                setStatus(HubConnectionState.Disconnected);
            });

        return () => {
            newConnection
                .stop()
                .then(() => {
                    setConnection(undefined);
                    setStatus(undefined);
                })
                .catch(error => {
                    console.error(`Error disconnecting from SignalR hub '${hubName}':`, error);
                });
        };
    }, [hubName, accessToken]);

    const listen = useCallback(
        (methodName: string, method: (...args: any[]) => any) => {
            if (!connection) return;

            connection.off(methodName);
            connection.on(methodName, method);
        },
        [connection]
    );

    return { connection, status, listen };
};
